import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/src/shadComponents/ui/dialog';
import { Button } from '@/src/shadComponents/ui/button';
import { Alert, AlertDescription } from '@/src/shadComponents/ui/alert';
import { Progress } from '@/src/shadComponents/ui/progress';
import {
    CreateDeviceRegistrationRequest,
    DeviceRegistrationAutoenroll,
} from './hooks/useDeviceRegistrationAutoenroll';

interface CsvImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (devices: CreateDeviceRegistrationRequest[]) => Promise<void>;
    existingDevices: DeviceRegistrationAutoenroll[];
}

interface ParsedDevice {
    deviceId: string;
    deviceName: string;
    groupNames?: string;
    isValid: boolean;
    errors: string[];
}

interface ImportSummary {
    validDevices: CreateDeviceRegistrationRequest[];
    duplicateDevices: ParsedDevice[];
    invalidDevices: ParsedDevice[];
    totalCount: number;
}

export default function CsvImportModal({
    isOpen,
    onClose,
    onImport,
    existingDevices,
}: CsvImportModalProps) {
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(
        null,
    );
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [importProgress, setImportProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setCsvFile(file);
            setImportSummary(null);
            setError(null);
        }
    };

    const parseCsvFile = (csvContent: string): ParsedDevice[] => {
        const lines = csvContent.trim().split('\n');
        const devices: ParsedDevice[] = [];

        // Skip header if present
        const startIndex = lines[0].toLowerCase().includes('device') ? 1 : 0;

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const columns = line
                .split(',')
                .map((col) => col.trim().replace(/"/g, ''));

            // Take all columns from index 2 onwards as group names
            const groupNames = columns
                .slice(2)
                .filter((group) => group.length > 0);

            const device: ParsedDevice = {
                deviceId: columns[0] || '',
                deviceName: columns[1] || '',
                groupNames: groupNames.join(','),
                isValid: true,
                errors: [],
            };

            // Validate device
            if (!device.deviceId) {
                device.isValid = false;
                device.errors.push('Device ID is required');
            }

            if (!device.deviceName) {
                device.isValid = false;
                device.errors.push('Device Name is required');
            }

            devices.push(device);
        }

        return devices;
    };

    const analyzeCsvFile = async () => {
        if (!csvFile) return;

        try {
            setIsProcessing(true);
            setError(null);

            const csvContent = await csvFile.text();
            const parsedDevices = parseCsvFile(csvContent);

            const existingDeviceIds = new Set(
                existingDevices.map((d) => d.deviceId),
            );

            const summary: ImportSummary = {
                validDevices: [],
                duplicateDevices: [],
                invalidDevices: [],
                totalCount: parsedDevices.length,
            };

            parsedDevices.forEach((device) => {
                if (!device.isValid) {
                    summary.invalidDevices.push(device);
                } else if (existingDeviceIds.has(device.deviceId)) {
                    summary.duplicateDevices.push(device);
                } else {
                    summary.validDevices.push({
                        deviceId: device.deviceId,
                        deviceName: device.deviceName,
                        groupNames: device.groupNames || undefined,
                    });
                }
            });

            setImportSummary(summary);
        } catch (err) {
            setError(
                'Failed to parse CSV file. Please check the format and try again.',
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleImport = async () => {
        if (!importSummary || importSummary.validDevices.length === 0) return;

        try {
            setIsProcessing(true);
            setImportProgress(0);

            // Simulate progress for user feedback
            const progressInterval = setInterval(() => {
                setImportProgress((prev) => Math.min(prev + 10, 90));
            }, 100);

            await onImport(importSummary.validDevices);

            clearInterval(progressInterval);
            setImportProgress(100);

            // Close modal after successful import
            setTimeout(() => {
                onClose();
                resetModal();
            }, 1000);
        } catch (err) {
            setError('Failed to import devices. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const resetModal = () => {
        setCsvFile(null);
        setImportSummary(null);
        setError(null);
        setImportProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        if (!isProcessing) {
            onClose();
            resetModal();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Import Devices from CSV</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {!importSummary && (
                        <div className="space-y-4">
                            <div>
                                <label
                                    htmlFor="csv-file"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Select CSV File
                                </label>
                                <input
                                    ref={fileInputRef}
                                    id="csv-file"
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-gray-500
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-full file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100"
                                />
                            </div>

                            <Alert>
                                <AlertDescription>
                                    <strong>CSV Format:</strong> The file should
                                    contain columns for deviceId, deviceName,
                                    and optionally multiple group names.
                                    <br />
                                    Example:{' '}
                                    <code>
                                        device123,Device
                                        Name,group1,group2,group3
                                    </code>
                                    <br />
                                    <br />
                                    <strong>
                                        For all iOS apps, the deviceId should be
                                        the serial number of the device. The
                                        device must be managed and configured
                                        with the `DeviceSerialNumber` key set in
                                        the `com.apple.configuration.managed`
                                        profile.
                                    </strong>
                                    <br />
                                    <small>
                                        All columns after deviceName will be
                                        treated as group names.
                                    </small>
                                </AlertDescription>
                            </Alert>

                            {csvFile && (
                                <Button
                                    onClick={analyzeCsvFile}
                                    disabled={isProcessing}
                                    className="w-full"
                                >
                                    {isProcessing
                                        ? 'Analyzing...'
                                        : 'Analyze CSV'}
                                </Button>
                            )}
                        </div>
                    )}

                    {importSummary && (
                        <div className="space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-medium text-gray-900 mb-3">
                                    Import Summary
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Total devices in file:</span>
                                        <span className="font-medium">
                                            {importSummary.totalCount}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-green-600">
                                        <span>Valid devices to import:</span>
                                        <span className="font-medium">
                                            {importSummary.validDevices.length}
                                        </span>
                                    </div>
                                    {importSummary.duplicateDevices.length >
                                        0 && (
                                        <div className="flex justify-between text-orange-600">
                                            <span>
                                                Duplicate devices (will be
                                                skipped):
                                            </span>
                                            <span className="font-medium">
                                                {
                                                    importSummary
                                                        .duplicateDevices.length
                                                }
                                            </span>
                                        </div>
                                    )}
                                    {importSummary.invalidDevices.length >
                                        0 && (
                                        <div className="flex justify-between text-red-600">
                                            <span>
                                                Invalid devices (will be
                                                skipped):
                                            </span>
                                            <span className="font-medium">
                                                {
                                                    importSummary.invalidDevices
                                                        .length
                                                }
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {importSummary.duplicateDevices.length > 0 && (
                                <Alert>
                                    <AlertDescription>
                                        <strong>Warning:</strong> The following
                                        devices already exist and will be
                                        skipped:
                                        <ul className="mt-2 list-disc list-inside text-sm">
                                            {importSummary.duplicateDevices
                                                .slice(0, 5)
                                                .map((device, index) => (
                                                    <li key={index}>
                                                        {device.deviceId} -{' '}
                                                        {device.deviceName}
                                                    </li>
                                                ))}
                                            {importSummary.duplicateDevices
                                                .length > 5 && (
                                                <li>
                                                    ... and{' '}
                                                    {importSummary
                                                        .duplicateDevices
                                                        .length - 5}{' '}
                                                    more
                                                </li>
                                            )}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {importSummary.invalidDevices.length > 0 && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        <strong>Errors:</strong> The following
                                        devices have validation errors:
                                        <ul className="mt-2 list-disc list-inside text-sm">
                                            {importSummary.invalidDevices
                                                .slice(0, 3)
                                                .map((device, index) => (
                                                    <li key={index}>
                                                        {device.deviceId ||
                                                            'N/A'}{' '}
                                                        -{' '}
                                                        {device.errors.join(
                                                            ', ',
                                                        )}
                                                    </li>
                                                ))}
                                            {importSummary.invalidDevices
                                                .length > 3 && (
                                                <li>
                                                    ... and{' '}
                                                    {importSummary
                                                        .invalidDevices.length -
                                                        3}{' '}
                                                    more
                                                </li>
                                            )}
                                        </ul>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {isProcessing && (
                                <div className="space-y-2">
                                    <div className="text-sm text-gray-600">
                                        Importing devices...
                                    </div>
                                    <Progress
                                        value={importProgress}
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isProcessing}
                    >
                        Cancel
                    </Button>
                    {importSummary && (
                        <Button
                            onClick={handleImport}
                            disabled={
                                isProcessing ||
                                importSummary.validDevices.length === 0
                            }
                            className="ml-2"
                        >
                            {isProcessing
                                ? 'Importing...'
                                : `Import ${
                                      importSummary.validDevices.length
                                  } Device${
                                      importSummary.validDevices.length !== 1
                                          ? 's'
                                          : ''
                                  }`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
